import React, { useState, useCallback } from "react";

interface CustomButtonProps {
  /** "sm" for a page-header or toolbar action; "md" is the page's primary button. */
  size?: 'sm' | 'md';
  width?: string;
  label?: string;
  handleOnClick?: () => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  className?: string;
  leftIcon?: React.ReactNode;
  loading?: boolean;
}

interface Ripple { id: number; x: number; y: number }

const restShadow  = '0px 1px 2px rgba(0,0,0,0.35), 0px 1px 3px 1px rgba(0,0,0,0.15)';
const hoverShadow = '0px 3px 6px rgba(0,0,0,0.25), 0px 2px 4px rgba(0,0,0,0.15)';

const CustomButton = ({
  size = 'md',
  width,
  label,
  handleOnClick,
  type = "button",
  disabled,
  className = "",
  leftIcon,
  loading = false,
}: CustomButtonProps) => {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || loading;
  const widthClass = width === "full" ? "w-full" : width ? `w-[${width}]` : "";
  // Written as whole class strings rather than pieces: Tailwind scans source text, so a class
  // assembled from fragments is a class it never generates.
  const sizeClass =
    size === 'sm'
      ? "min-h-[34px] px-4 text-[13px] gap-1.5"
      : "min-h-[44px] px-6 text-[15px] gap-2";

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const id = Date.now();
    setRipples(prev => [...prev, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 600);
    handleOnClick?.();
  }, [handleOnClick]);

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        boxShadow: hovered ? hoverShadow : restShadow,
        transition: 'box-shadow 0.2s ease, background-color 0.2s ease, transform 0.1s ease',
      }}
      className={`relative overflow-hidden inline-flex items-center justify-center bg-pcg-gold text-black font-proxima-bold rounded-[20px] tracking-[0.01em] active:scale-[0.96] outline-none select-none hover:bg-pcg-goldDark ${sizeClass} ${isDisabled ? "opacity-40 cursor-not-allowed pointer-events-none" : "cursor-pointer"} ${widthClass} ${className}`}
    >
      {ripples.map(r => (
        <span key={r.id} className="btn-ripple" style={{ left: r.x, top: r.y }} />
      ))}
      {loading ? (
        <svg className="animate-spin shrink-0" width={16} height={16} viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      ) : leftIcon && (
        <span className="shrink-0 flex items-center">{leftIcon}</span>
      )}
      <span className="truncate">{label}</span>
    </button>
  );
};

export default CustomButton;